import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import {
  PatientListFilterComponentProps,
  PatientListFiltersData,
} from '@lmt/unicorn-shared/app/components/patients/list/common/types';
import { FieldsWithType } from '@lmt/unicorn-shared/app/helpers/typeUtils';
import { IconButton, Menu, MenuItem, useEventCallback } from '@mui/material';
import { ReactComponent as OptionIcon } from '@lmt/unicorn-shared/app/icons/option-big.svg';
import { PopoverOrigin } from '@mui/material/Popover/Popover';
import clsx from 'clsx';
import React, { FC, useMemo, useState } from 'react';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import styles from './OptionButton.module.scss';

export type TogglableOptions = FieldsWithType<
  PatientListFiltersData,
  boolean | null | undefined
>;
interface Props {
  options: TogglableOptions[];
}

export const OptionButton: FC<Props & PatientListFilterComponentProps> =
  React.memo(function OptionButton(props) {
    const { options, values, onChange } = props;
    const i18next = useScopedTranslation('patients.list.filter.toggles');
    const [anchorEl, setAnchorEl] = useState<Element | null>(null);

    const handleClose = useEventCallback(() => {
      setAnchorEl(null);
    });

    const optionValues = useMemo(
      () => options.map((option) => ({ option, value: values?.[option] })),
      [options, values],
    );

    const menuOptions = useMemo(
      () =>
        optionValues.map((optionValue) => {
          const callback = () =>
            onChange({
              [optionValue.option]: !optionValue.value,
            });
          return (
            <div key={optionValue.option}>
              <MenuItem
                disableTouchRipple={true}
                disableRipple={true}
                divider={true}
                className={styles.optionContainer}
                onClick={callback}
              >
                <CheckBox
                  checked={!!optionValue.value}
                  onChange={callback}
                  title={i18next.t(optionValue.option)}
                />
              </MenuItem>
            </div>
          );
        }),
      [optionValues],
    );

    const anchorOrigin: PopoverOrigin = useMemo(
      () => ({
        vertical: 'bottom',
        horizontal: 'center',
      }),
      [],
    );
    const transformOrigin: PopoverOrigin = useMemo(
      () => ({
        vertical: 'top',
        horizontal: 'right',
      }),
      [],
    );
    return (
      <div className={styles.iconContainer}>
        <IconButton
          disableFocusRipple={true}
          disableTouchRipple={true}
          disableRipple={true}
          className={clsx(
            styles.optionButton,
            Boolean(anchorEl) && styles.optionButtonHover,
          )}
          aria-label="more"
          aria-controls="filter-option-menu"
          aria-haspopup="true"
          onClick={(event: any) => setAnchorEl(event.currentTarget)}
        >
          <OptionIcon
            className={styles.optionButtonIcon}
            data-test-id="patient-list-filter-option-button"
          />
        </IconButton>
        <Menu
          id="filter-option-menu"
          anchorEl={anchorEl}
          disablePortal={true}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
        >
          {menuOptions}
        </Menu>
      </div>
    );
  });
